<template>
  <v-dialog
      v-model="dialog"
      persistent
      max-height="680"
      scrollable
      :fullscreen="$vuetify.display.xs"
      :width="$vuetify.display.smAndUp ? 600 : 'auto'"
      style="z-index: 1100 !important;"
      :disabled="disabled"
  >

    <template v-slot:activator="{ props }">
      <div v-bind="props" data-cy="list-tenantLabels-createLabel-activator">
        <slot></slot>
      </div>
    </template>

    <v-form ref="form"
      v-on:submit.prevent="updateLabel"
      autocomplete="off"
      data-cy="dialog-createLabel-form">

      <v-card v-cloak data-cy="dialog-tenantLabels-modal">
        <v-card-title class="d-flex justify-space-between mb-2">
          <span v-if="this.hasLabel">{{$t('routes.labels.editLabel')}}</span>
          <span v-else>{{$t('routes.labels.createLabel')}}</span>

          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text scrollable class="pt-4" data-cy="dialog------">
          <div>
            <v-label>
              {{ $t('routes.labels.labelName') }}:
            </v-label>
            <v-text-field
                v-model="labelName"
                v-bind:label="$t('routes.labels.labelName')"
                v-bind:rules="labelNameRules"
                required block
                variant="solo-filled" flat
                data-cy="dialog-tenantLabels-labelName">
            </v-text-field>

            <v-label>
              {{ $t('routes.labels.selectCategory') }}:
            </v-label>
            <v-select
                :items="flatCategories"
                v-model="selectedCategory"
                v-bind:rules="categoryRules"
                item-title="name"
                item-value="id"
                variant="solo-filled" flat
            ></v-select>
          </div>

          <v-alert v-if="errorMessage" type="error" data-cy="dialog-tenantLabels-errorMessage">
            {{errorMessage}}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
              type="submit"
              class="bg-primary"
              elevation=0
              data-cy="dialog-tenantLabels-submitButton"
          >
            {{ hasLabel ? $t('common.update') : $t('common.create') }}
          </v-btn>

          <v-btn variant="text" @click="dialog = false" data-cy="dialog-tenantLabels-closeButton">
            {{$t('common.cancel')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useLabelStore } from '@/stores/LabelStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  emits: ["labelUpdated"],
  mixins: [DialogActionKeys],
  name: "CreateUpdateLabelDialog",

  props: {
    label: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    const namePattern = /^[\p{L}\p{N}\s-]{2,}$/u;

    return {
      dialog: false,

      errorMessage: "",

      labelName: "",
      labelNameRules: [
        v => !!v || this.$t('routes.labels.labelNameRequired'),
        v => namePattern.test(v) || this.$t('routes.labels.invalidLabelName'),
      ],
      selectedCategory: null,
      categoryRules: [
        v => !!v || this.$t('routes.labels.categoryRequired'),
      ],
    };
  },

  mounted: function() {
    this.resetForm();
  },

  watch: {
    dialog() {
      this.resetForm();
    },
    label: function() {
      this.resetForm();
    }
  },

  methods: {
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      this.$nextTick(() => {
        this.errorMessage = "";

        if (this?.label?.id) {
          this.labelName = this.label?.content || '';
          this.selectedCategory = this.label?.categoryid || null;
        } else {
          this.selectedCategory = this.activeCategoryId || null;
        }
      });
    },
    async updateLabel() {
      this.errorMessage = "";

      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      let payload = {
        label: this.labelName,
        accessPointId: this.accessPointId,
        categoryId: this.selectedCategory || null,
      };

      const labelStore = useLabelStore();

      let messageToShow = this.$t('routes.labels.labelCreated');
      let ACTION = labelStore.createLabel;
      if (this.label.id) {
        payload.labelId = this.label.id;
        ACTION = labelStore.updateLabel;
        messageToShow = this.$t('routes.labels.labelUpdated');
      }

      try {
        await ACTION(payload);
        this.$showMessage(messageToShow);
        this.$emit('labelUpdated', this.selectedCategory);
        this.dialog = false;
      } catch (error) {
        const apiError = error?.response?.data?.error?.message;
        console.error(apiError);

        if (apiError) {
          this.errorMessage = this.$localizeMessage(apiError);
        }
      }
    },
  },

  computed: {
    accessPointId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    categories: function() {
      const labelStore = useLabelStore();
      return labelStore.getCategories;
    },
    flatCategories() {
      const flattenCategories = (categories, level = 0) => {
        return categories.reduce((acc, category) => {
          let name = category.name;

          if (level > 0) {
            name = '└'+'─'.repeat(level * 2) + ' ' + category.name;
          }

          acc.push({
            ...category,
            name,
            level,
          });
          if (category.children) {
            acc = acc.concat(flattenCategories(category.children, level + 1));
          }
          return acc;
        }, []);
      };
      return flattenCategories(this.categories);
    },
    hasLabel() {
      return !!(this.label && this.label.id);
    },
    activeCategoryId() {
      const labelStore = useLabelStore();
      return labelStore.getActiveCategoryId;
    },
  }
};
</script>

<style>
</style>
